/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { CreateEmailTemplateRequest } from '../models/create-email-template-request';
import { EmailMessage } from '../models/email-message';
import { EmailTemplate } from '../models/email-template';
import { RenderEmailTemplateRequest } from '../models/render-email-template-request';
import { RenderedEmailContent } from '../models/rendered-email-content';
import { SendEmailRequest } from '../models/send-email-request';
import { UpdateEmailTemplateRequest } from '../models/update-email-template-request';


/**
 * API for managing emails and webhooks.
 */
@Injectable({
  providedIn: 'root',
})
export class EmailsService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation listEmails
   */
  static readonly ListEmailsPath = '/invoices/{id}/emails';

  /**
   * List emails for an invoice.
   *
   * Returns a unified list of emails associated with an invoice, including legacy logs.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listEmails()` instead.
   *
   * This method doesn't expect any request body.
   */
  listEmails$Response(params: {
    id: number;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<Array<EmailMessage>>> {

    const rb = new RequestBuilder(this.rootUrl, EmailsService.ListEmailsPath, 'get');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<EmailMessage>>;
      })
    );
  }

  /**
   * List emails for an invoice.
   *
   * Returns a unified list of emails associated with an invoice, including legacy logs.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `listEmails$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listEmails(params: {
    id: number;
    context?: HttpContext
  }
): Observable<Array<EmailMessage>> {

    return this.listEmails$Response(params).pipe(
      map((r: StrictHttpResponse<Array<EmailMessage>>) => r.body as Array<EmailMessage>)
    );
  }

  /**
   * Path part for operation sendEmail
   */
  static readonly SendEmailPath = '/invoices/{id}/emails';

  /**
   * Send an email for an invoice.
   *
   * Renders a template and sends an email via SMTP.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `sendEmail()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  sendEmail$Response(params: {
    id: number;
    context?: HttpContext
    body: SendEmailRequest
  }
): Observable<StrictHttpResponse<EmailMessage>> {

    const rb = new RequestBuilder(this.rootUrl, EmailsService.SendEmailPath, 'post');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<EmailMessage>;
      })
    );
  }

  /**
   * Send an email for an invoice.
   *
   * Renders a template and sends an email via SMTP.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `sendEmail$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  sendEmail(params: {
    id: number;
    context?: HttpContext
    body: SendEmailRequest
  }
): Observable<EmailMessage> {

    return this.sendEmail$Response(params).pipe(
      map((r: StrictHttpResponse<EmailMessage>) => r.body as EmailMessage)
    );
  }

  /**
   * Path part for operation listEmailTemplates
   */
  static readonly ListEmailTemplatesPath = '/email-templates';

  /**
   * List all email templates.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listEmailTemplates()` instead.
   *
   * This method doesn't expect any request body.
   */
  listEmailTemplates$Response(params?: {
    context?: HttpContext
  }
): Observable<StrictHttpResponse<Array<EmailTemplate>>> {

    const rb = new RequestBuilder(this.rootUrl, EmailsService.ListEmailTemplatesPath, 'get');
    if (params) {
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<EmailTemplate>>;
      })
    );
  }

  /**
   * List all email templates.
   *
   *
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `listEmailTemplates$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listEmailTemplates(params?: {
    context?: HttpContext
  }
): Observable<Array<EmailTemplate>> {

    return this.listEmailTemplates$Response(params).pipe(
      map((r: StrictHttpResponse<Array<EmailTemplate>>) => r.body as Array<EmailTemplate>)
    );
  }

  /**
   * Path part for operation createEmailTemplate
   */
  static readonly CreateEmailTemplatePath = '/email-templates';

  /**
   * Create a new email template.
   *
   * Requires the `templates:manage` permission (403 otherwise).
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createEmailTemplate()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createEmailTemplate$Response(params: {
    context?: HttpContext
    body: CreateEmailTemplateRequest
  }
): Observable<StrictHttpResponse<EmailTemplate>> {

    const rb = new RequestBuilder(this.rootUrl, EmailsService.CreateEmailTemplatePath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<EmailTemplate>;
      })
    );
  }

  /**
   * Create a new email template.
   *
   * Requires the `templates:manage` permission (403 otherwise).
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `createEmailTemplate$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createEmailTemplate(params: {
    context?: HttpContext
    body: CreateEmailTemplateRequest
  }
): Observable<EmailTemplate> {

    return this.createEmailTemplate$Response(params).pipe(
      map((r: StrictHttpResponse<EmailTemplate>) => r.body as EmailTemplate)
    );
  }

  /**
   * Path part for operation updateEmailTemplate
   */
  static readonly UpdateEmailTemplatePath = '/email-templates/{templateId}';

  /**
   * Update an existing email template.
   *
   * Requires the `templates:manage` permission (403 otherwise).
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateEmailTemplate()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateEmailTemplate$Response(params: {
    templateId: number;
    context?: HttpContext
    body: UpdateEmailTemplateRequest
  }
): Observable<StrictHttpResponse<EmailTemplate>> {

    const rb = new RequestBuilder(this.rootUrl, EmailsService.UpdateEmailTemplatePath, 'put');
    if (params) {
      rb.path('templateId', params.templateId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<EmailTemplate>;
      })
    );
  }

  /**
   * Update an existing email template.
   *
   * Requires the `templates:manage` permission (403 otherwise).
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateEmailTemplate$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateEmailTemplate(params: {
    templateId: number;
    context?: HttpContext
    body: UpdateEmailTemplateRequest
  }
): Observable<EmailTemplate> {

    return this.updateEmailTemplate$Response(params).pipe(
      map((r: StrictHttpResponse<EmailTemplate>) => r.body as EmailTemplate)
    );
  }

  /**
   * Path part for operation renderEmailTemplate
   */
  static readonly RenderEmailTemplatePath = '/email-templates/render';

  /**
   * Render an email template with invoice data.
   *
   * Replaces preview-safe template placeholders with actual invoice data and returns the rendered subject and content. Send-time-only variables (such as verification PINs) are left as literal {tag} text and will be resolved when the email is actually sent. This is a preview — it does not send anything or generate PINs.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `renderEmailTemplate()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  renderEmailTemplate$Response(params: {
    context?: HttpContext
    body: RenderEmailTemplateRequest
  }
): Observable<StrictHttpResponse<RenderedEmailContent>> {

    const rb = new RequestBuilder(this.rootUrl, EmailsService.RenderEmailTemplatePath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<RenderedEmailContent>;
      })
    );
  }

  /**
   * Render an email template with invoice data.
   *
   * Replaces preview-safe template placeholders with actual invoice data and returns the rendered subject and content. Send-time-only variables (such as verification PINs) are left as literal {tag} text and will be resolved when the email is actually sent. This is a preview — it does not send anything or generate PINs.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `renderEmailTemplate$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  renderEmailTemplate(params: {
    context?: HttpContext
    body: RenderEmailTemplateRequest
  }
): Observable<RenderedEmailContent> {

    return this.renderEmailTemplate$Response(params).pipe(
      map((r: StrictHttpResponse<RenderedEmailContent>) => r.body as RenderedEmailContent)
    );
  }

}
